import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";

function isSellerPortalHost(host: string) {
  const normalizedHost = (host.split(":")[0] ?? "").trim().toLowerCase();
  return normalizedHost.startsWith("seller.") || normalizedHost.startsWith("seller-app.");
}

function readEnvironmentValue(name: string) {
  const env = (
    import.meta as ImportMeta & { env?: Record<string, string | boolean | undefined> }
  ).env ?? {};
  if (name === "NODE_ENV") {
    if (env.PROD === true) {
      return "production";
    }

    if (env.DEV === true) {
      return "development";
    }
  }

  if (typeof process !== "undefined") {
    return process.env?.[name];
  }

  return env[name];
}

export function PwaBootstrap() {
  const t = useTranslations();
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const updateRequestedRef = useRef(false);
  const reloadedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    if (!isSellerPortalHost(window.location.host)) {
      return;
    }

    const nodeEnv = readEnvironmentValue("NODE_ENV");

    if (nodeEnv !== "production") {
      void navigator.serviceWorker
        .getRegistrations()
        .then((registrations) =>
          Promise.all(registrations.map((registration) => registration.unregister()))
        )
        .catch(() => null);

      if ("caches" in window) {
        void caches
          .keys()
          .then((keys) =>
            Promise.all(
              keys
                .filter((key) => key.startsWith("seller-static-"))
                .map((key) => caches.delete(key))
            )
          )
          .catch(() => null);
      }

      return;
    }

    let disposed = false;

    // Reload once the new worker takes control, but only when the user asked
    // for the update (avoids reloading on first install with clientsClaim,
    // and the flag guards against reload loops).
    const handleControllerChange = () => {
      if (!updateRequestedRef.current || reloadedRef.current) {
        return;
      }
      reloadedRef.current = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange);

    navigator.serviceWorker
      .register("/seller-sw.js")
      .then((registration) => {
        if (disposed) {
          return;
        }

        // An updated worker was already waiting when the page loaded.
        if (registration.waiting && navigator.serviceWorker.controller) {
          setWaitingWorker(registration.waiting);
        }

        // A new worker was found while this page is open.
        registration.addEventListener("updatefound", () => {
          const installing = registration.installing;
          if (!installing) {
            return;
          }

          installing.addEventListener("statechange", () => {
            if (
              !disposed &&
              installing.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              setWaitingWorker(registration.waiting ?? installing);
            }
          });
        });
      })
      .catch(() => null);

    return () => {
      disposed = true;
      navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange);
    };
  }, []);

  const handleReload = useCallback(() => {
    if (!waitingWorker) {
      return;
    }
    updateRequestedRef.current = true;
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
  }, [waitingWorker]);

  if (!waitingWorker) {
    return null;
  }

  return (
    <div
      role="status"
      className="fixed inset-x-4 bottom-[calc(4rem+env(safe-area-inset-bottom)+1rem)] z-50 mx-auto flex max-w-md items-center justify-between gap-3 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 shadow-lg lg:bottom-4"
    >
      <span className="text-sm text-[color:var(--text-primary)]">
        {t(keys.pwa.updateAvailable)}
      </span>
      <button
        type="button"
        onClick={handleReload}
        className="shrink-0 rounded-lg bg-[color:var(--primary-500)] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-[color:var(--primary-600)]"
      >
        {t(keys.pwa.reload)}
      </button>
    </div>
  );
}
